import React from 'react';
import { Activity, ClipboardList, Baby, Utensils, Stethoscope } from 'lucide-react';
import { Card, CardContent, CardHeader } from './ui/Card';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { SectionHeader } from './ui/SectionHeader';
import { MedicalFormData } from '../hooks/useFormState';
interface PhysicalExaminationProps {
  data: MedicalFormData;
  onChange: (field: keyof MedicalFormData, value: any) => void;
}
export function PhysicalExamination({
  data,
  onChange
}: PhysicalExaminationProps) {
  const handleFoodHabitToggle = (habit: string) => {
    const current = data.foodHabits || [];
    const updated = current.includes(habit) ? current.filter(h => h !== habit) : [...current, habit];
    onChange('foodHabits', updated);
  };
  const handleMedicalHistoryToggle = (condition: string) => {
    const current = data.medicalHistory || [];
    const updated = current.includes(condition) ? current.filter(c => c !== condition) : [...current, condition];
    onChange('medicalHistory', updated);
  };
  const handleMentalConditionToggle = (condition: string) => {
    const current = data.mentalCondition || [];
    const updated = current.includes(condition) ? current.filter(c => c !== condition) : [...current, condition];
    onChange('mentalCondition', updated);
  };
  const handlePainfulToggle = (option: string) => {
    const current = data.mensurationPainful || [];
    const updated = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
    onChange('mensurationPainful', updated);
  };
  const handleGynecologicalConditionToggle = (condition: string) => {
    const current = data.gynecologicalConditions || [];
    const updated = current.includes(condition) ? current.filter(c => c !== condition) : [...current, condition];
    onChange('gynecologicalConditions', updated);
  };
  // Check if Gynaecological History should be shown
  const showGynecologicalHistory = data.gender === 'F';
  return <Card className="h-full" data-id="element-226">
      <CardHeader data-id="element-227">
        <SectionHeader title="Physical Examination" subtitle="General survey and objective findings" icon={<Activity className="w-5 h-5" data-id="element-229" />} data-id="element-228" />
      </CardHeader>
      <CardContent className="space-y-8" data-id="element-230">
        {/* General Survey Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" data-id="element-231">
          <div className="space-y-4" data-id="element-232">
            <div data-id="element-233">
              <Label data-id="element-234">Thirst (प्यास)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.thirst} onChange={e => onChange('thirst', e.target.value)} data-id="element-235">
                <option value="" data-id="element-236">Select Water Intake</option>
                <option value="1-2 Glass" data-id="element-237">1-2 Glass</option>
                <option value="3-4 Glass" data-id="element-238">3-4 Glass</option>
                <option value="4-5 Glass" data-id="element-239">4-5 Glass</option>
                <option value="6-7 Glass" data-id="element-240">6-7 Glass</option>
                <option value="7-8 Glass" data-id="element-241">7-8 Glass</option>
                <option value="8-10 Glass" data-id="element-242">8-10 Glass</option>
                <option value="10+ Glass" data-id="element-243">10+ Glass</option>
              </select>
            </div>

            <div data-id="element-244">
              <Label data-id="element-245">Appetite (भूख)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.appetite} onChange={e => onChange('appetite', e.target.value)} data-id="element-246">
                <option value="" data-id="element-247">Select Appetite</option>
                <option value="Normal Appetite" data-id="element-248">Normal Appetite</option>
                <option value="Poor Appetite and Loss of Appetite" data-id="element-249">
                  Poor Appetite and Loss of Appetite
                </option>
                <option value="Excess Appetite" data-id="element-250">Excess Appetite</option>
                <option value="Hungry but can't eat" data-id="element-251">
                  Hungry but can't eat
                </option>
              </select>
            </div>

            <div data-id="element-252">
              <Label data-id="element-253">Sleep (नींद)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.sleep} onChange={e => onChange('sleep', e.target.value)} data-id="element-254">
                <option value="" data-id="element-255">Select Sleep Pattern</option>
                <option value="Normal Sleep" data-id="element-256">Normal Sleep</option>
                <option value="Difficulty Falling Asleep" data-id="element-257">
                  Difficulty Falling Asleep
                </option>
                <option value="Difficulty falling Asleep after Awake" data-id="element-258">
                  Difficulty falling Asleep after Awake
                </option>
                <option value="Frequent Waking" data-id="element-259">Frequent Waking</option>
                <option value="Light or Unrestful Sleep" data-id="element-260">
                  Light or Unrestful Sleep
                </option>
                <option value="Dreams Disturbed" data-id="element-261">
                  Dreams Disturbed (सपनों से परेशान)
                </option>
              </select>
            </div>

            <div data-id="element-262">
              <Label data-id="element-263">Dreams (सपने)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.dreams} onChange={e => onChange('dreams', e.target.value)} data-id="element-264">
                <option value="" data-id="element-265">Select Dream Pattern</option>
                <option value="No Dreams" data-id="element-266">No Dreams</option>
                <option value="Vivid or Disturbed Dreams" data-id="element-267">
                  Vivid or Disturbed Dreams
                </option>
                <option value="Nightmares" data-id="element-268">Nightmares</option>
                <option value="Dream Categories (fearful, surprise, etc)" data-id="element-269">
                  Dream Categories (fearful, surprise, etc)
                </option>
              </select>
            </div>

            <div data-id="element-270">
              <Label data-id="element-271">Addiction</Label>
              <Input value={data.addiction} onChange={e => onChange('addiction', e.target.value)} data-id="element-272" />
            </div>

            <div data-id="element-273">
              <Label data-id="element-274">Blood Pressure (रक्तचाप)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.bp} onChange={e => onChange('bp', e.target.value)} data-id="element-275">
                <option value="" data-id="element-276">Select Blood Pressure</option>
                <option value="Normal" data-id="element-277">Normal</option>
                <option value="Low" data-id="element-278">Low</option>
                <option value="High" data-id="element-279">High</option>
              </select>
            </div>
          </div>

          <div className="space-y-4" data-id="element-280">
            <div data-id="element-281">
              <Label data-id="element-282">Hygiene</Label>
              <Input value={data.hygiene} onChange={e => onChange('hygiene', e.target.value)} data-id="element-283" />
            </div>

            <div data-id="element-284">
              <Label data-id="element-285">Life Style</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.lifestyle} onChange={e => onChange('lifestyle', e.target.value)} data-id="element-286">
                <option value="" data-id="element-287">Select Lifestyle</option>
                <option value="Sedentary lifestyle prolonged periods of sitting" data-id="element-288">
                  Sedentary lifestyle prolonged periods of sitting
                </option>
                <option value="Lightly Active" data-id="element-289">Lightly Active</option>
                <option value="Moderately Active" data-id="element-290">Moderately Active</option>
                <option value="Vigorously Active" data-id="element-291">Vigorously Active</option>
              </select>
            </div>

            <div data-id="element-292">
              <Label data-id="element-293">Body Type (शरीर के प्रकार)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.bodyType} onChange={e => onChange('bodyType', e.target.value)} data-id="element-294">
                <option value="" data-id="element-295">Select Body Type</option>
                <option value="Underweight" data-id="element-296">Underweight (कम वजन)</option>
                <option value="Normal weight" data-id="element-297">
                  Normal weight (सामान्य वजन)
                </option>
                <option value="Overweight" data-id="element-298">Overweight (अधिक वजन)</option>
                <option value="Obese" data-id="element-299">Obese (मोटापा)</option>
                <option value="Huge" data-id="element-300">Huge (बहुत)</option>
              </select>
            </div>

            <div data-id="element-301">
              <Label data-id="element-302">Mental Condition (मानसिक हालत) - Select Multiple</Label>
              <div className="space-y-2 bg-amber-50/50 p-4 rounded-lg border-2 border-amber-200 max-h-48 overflow-y-auto" data-id="element-303">
                {['Normal', 'Good', 'Tension', 'Anxiety', 'Confused', 'Worried', 'Depressed', 'Other'].map(condition => <label key={condition} className="flex items-center space-x-3 cursor-pointer hover:bg-amber-100/50 p-2 rounded transition-colors" data-id="element-304">
                    <input type="checkbox" className="rounded border-amber-400 text-amber-600 focus:ring-amber-500" checked={(data.mentalCondition || []).includes(condition)} onChange={() => handleMentalConditionToggle(condition)} data-id="element-305" />
                    <span className="text-sm text-stone-700" data-id="element-306">{condition}</span>
                  </label>)}
              </div>
              {(data.mentalCondition || []).includes('Other') && <Input className="mt-2" value={data.mentalConditionOther} onChange={e => onChange('mentalConditionOther', e.target.value)} placeholder="Please specify..." data-id="element-307" />}
            </div>

            <div className="grid grid-cols-2 gap-2" data-id="element-308">
              <div data-id="element-309">
                <Label data-id="element-310">Weight</Label>
                <Input value={data.weight} onChange={e => onChange('weight', e.target.value)} placeholder="kg" data-id="element-311" />
              </div>
              <div data-id="element-312">
                <Label data-id="element-313">Temperature (तापमान)</Label>
                <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.temperature} onChange={e => onChange('temperature', e.target.value)} data-id="element-314">
                  <option value="" data-id="element-315">Select</option>
                  <option value="Normal" data-id="element-316">Normal</option>
                  <option value="Low" data-id="element-317">Low</option>
                  <option value="High" data-id="element-318">High</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Food Habits Section */}
        <div className="pt-6 border-t-2 border-amber-100" data-id="element-319">
          <div className="flex items-center gap-2 mb-4" data-id="element-320">
            <Utensils className="w-5 h-5 text-amber-600" data-id="element-321" />
            <h3 className="text-base font-bold text-amber-900" data-id="element-322">
              Food Habits and Nutrition (भोजन की आदतें और पोषण)
            </h3>
          </div>
          <div className="space-y-3 bg-amber-50/50 p-4 rounded-lg border border-amber-200" data-id="element-323">
            {['Take Fruits and Dry fruits & Nuts (soaked over night) (फल और सूखे मेवे और नट्स (रात भर भिगोए हुए))', 'Take 3 Times Healthy Food (3 बार स्वस्थ भोजन लें)', "Don't take water before any meal before and after 45 min (किसी भी भोजन से पहले और बाद में 45 मिनट तक पानी न पिएं)", 'Take water before any meal before and after 45 min (किसी भी भोजन से पहले और बाद में 45 मिनट पानी पिएं)'].map(habit => <label key={habit} className="flex items-start space-x-3 cursor-pointer hover:bg-amber-100/50 p-2 rounded transition-colors" data-id="element-324">
                <input type="checkbox" className="mt-1 rounded border-amber-400 text-amber-600 focus:ring-amber-500" checked={(data.foodHabits || []).includes(habit)} onChange={() => handleFoodHabitToggle(habit)} data-id="element-325" />
                <span className="text-sm text-stone-700 leading-relaxed" data-id="element-326">
                  {habit}
                </span>
              </label>)}
          </div>
        </div>

        {/* Vitals Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-100" data-id="element-327">
          <div data-id="element-328">
            <Label data-id="element-329">Body Palpation</Label>
            <Input value={data.bodyPalpation} onChange={e => onChange('bodyPalpation', e.target.value)} data-id="element-330" />
          </div>
          <div data-id="element-331">
            <Label data-id="element-332">Tongue</Label>
            <Input value={data.tongue} onChange={e => onChange('tongue', e.target.value)} data-id="element-333" />
          </div>
          <div data-id="element-334">
            <Label data-id="element-335">Pulse</Label>
            <Input value={data.pulse} onChange={e => onChange('pulse', e.target.value)} data-id="element-336" />
          </div>
        </div>

        {/* Medical History Section */}
        <div className="pt-6 border-t-2 border-amber-200" data-id="element-337">
          <div className="flex items-center gap-2 mb-4" data-id="element-338">
            <Stethoscope className="w-5 h-5 text-amber-600" data-id="element-339" />
            <h3 className="text-lg font-bold text-amber-900" data-id="element-340">
              Medical History
            </h3>
          </div>
          <p className="text-sm text-stone-600 mb-4" data-id="element-341">
            Are you Suffering or Suffered in Past with any of the disease(s)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 bg-amber-50/50 p-5 rounded-lg border-2 border-amber-200" data-id="element-342">
            {['None', 'Pre-Diabetic', 'Kidney Related Problem', 'Arthritis', 'HIV/AIDS', 'Stroke', "Alzheimer's Disease", 'Any Organ Transplant', 'Diabetic', 'Heart Related Problem', 'High Blood Pressure', 'Cancer', 'Hepatitis', 'Paralysis', 'Asthma'].map(condition => <label key={condition} className="flex items-center space-x-3 cursor-pointer hover:bg-amber-100/50 p-2 rounded transition-colors" data-id="element-343">
                <input type="checkbox" className="rounded border-amber-400 text-amber-600 focus:ring-amber-500" checked={(data.medicalHistory || []).includes(condition)} onChange={() => handleMedicalHistoryToggle(condition)} data-id="element-344" />
                <span className="text-sm text-stone-700" data-id="element-345">{condition}</span>
              </label>)}
          </div>
        </div>

        {/* Objective Findings Section */}
        <div className="pt-6 border-t border-slate-100" data-id="element-346">
          <SectionHeader title="Objective Findings" icon={<ClipboardList className="w-4 h-4" data-id="element-348" />} data-id="element-347" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" data-id="element-349">
            <div data-id="element-350">
              <Label data-id="element-351">1. Bowel Movement (मल त्याग)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.bowel} onChange={e => onChange('bowel', e.target.value)} data-id="element-352">
                <option value="" data-id="element-353">Select Option</option>
                <option value="1 time in a day (morning)" data-id="element-354">
                  1 time in a day (morning) (दिन में 1 बार (सुबह))
                </option>
                <option value="1 Time in a day(Any time)" data-id="element-355">
                  1 Time in a day(Any time) (दिन में 1 बार (किसी भी समय))
                </option>
                <option value="2 Times in a day (Morning / Evening)" data-id="element-356">
                  2 Times in a day (Morning / Evening) (दिन में 2 बार (सुबह /
                  शाम))
                </option>
                <option value="After meal" data-id="element-357">After meal (भोजन के बाद)</option>
                <option value="Before meal" data-id="element-358">Before meal (भोजन से पहले)</option>
                <option value="Other" data-id="element-359">Other (अन्य)</option>
              </select>
              {data.bowel === 'Other' && <Input className="mt-2" value={data.bowelOther} onChange={e => onChange('bowelOther', e.target.value)} placeholder="Please specify..." data-id="element-360" />}
              <Input className="mt-2" value={data.bowelAdditional} onChange={e => onChange('bowelAdditional', e.target.value)} placeholder="Additional details..." data-id="element-361" />
            </div>

            <div data-id="element-362">
              <Label data-id="element-363">
                2. Urine Frequency (In Day) (दिन में पेशाब की आवृत्ति)
              </Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.urineFrequency} onChange={e => onChange('urineFrequency', e.target.value)} data-id="element-364">
                <option value="" data-id="element-365">Select Option</option>
                <option value="No" data-id="element-366">No (नहीं)</option>
                <option value="Normal" data-id="element-367">Normal (सामान्य)</option>
                <option value="Excess" data-id="element-368">Excess (अधिक)</option>
                <option value="Less" data-id="element-369">Less (कम)</option>
              </select>
            </div>

            <div data-id="element-370">
              <Label data-id="element-371">
                3. Urine Frequency (In Night) (रात में पेशाब की आवृत्ति)
              </Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.urineFrequencyNight} onChange={e => onChange('urineFrequencyNight', e.target.value)} data-id="element-372">
                <option value="" data-id="element-373">Select Option</option>
                <option value="No" data-id="element-374">No (नहीं)</option>
                <option value="Normal" data-id="element-375">Normal (सामान्य)</option>
                <option value="Excess" data-id="element-376">Excess (अधिक)</option>
                <option value="Less" data-id="element-377">Less (कम)</option>
              </select>
            </div>

            <div data-id="element-378">
              <Label data-id="element-379">4. Urine Color (मूत्र का रंग)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.urineColor} onChange={e => onChange('urineColor', e.target.value)} data-id="element-380">
                <option value="" data-id="element-381">Select Color</option>
                <option value="Pale Yellow" data-id="element-382">हल्का पीला (Pale Yellow)</option>
                <option value="Dark Yellow" data-id="element-383">गहरा पीला (Dark Yellow)</option>
                <option value="Orange" data-id="element-384">नारंगी (Orange)</option>
                <option value="Pink or Red" data-id="element-385">गुलाबी या लाल (Pink or Red)</option>
                <option value="Green or Blue" data-id="element-386">
                  हरा या नीला (Green or Blue)
                </option>
                <option value="Brown" data-id="element-387">भूरा (Brown)</option>
                <option value="White" data-id="element-388">सफेद (White)</option>
              </select>
            </div>

            <div data-id="element-389">
              <Label data-id="element-390">5. Urine Smell (मूत्र की गंध)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.urineSmell} onChange={e => onChange('urineSmell', e.target.value)} data-id="element-391">
                <option value="" data-id="element-392">Select Smell</option>
                <option value="Normal Smell" data-id="element-393">Normal Smell (सामान्य गंध)</option>
                <option value="Strong Smell" data-id="element-394">तीव्र गंध (Strong Smell)</option>
              </select>
            </div>

            <div data-id="element-395">
              <Label data-id="element-396">6. Urine Details (Additional)</Label>
              <Input value={data.urine} onChange={e => onChange('urine', e.target.value)} placeholder="Burning / Other details" data-id="element-397" />
            </div>

            <div data-id="element-398">
              <Label data-id="element-399">7. Skin (त्वचा)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.skin} onChange={e => onChange('skin', e.target.value)} data-id="element-400">
                <option value="" data-id="element-401">Select Skin Type</option>
                <option value="Normal Skin" data-id="element-402">सामान्य त्वचा (Normal Skin)</option>
                <option value="Oily Skin" data-id="element-403">तैलीय त्वचा (Oily Skin)</option>
                <option value="Dry Skin" data-id="element-404">शुष्क त्वचा (Dry Skin)</option>
                <option value="Combination Skin" data-id="element-405">
                  मिश्रित त्वचा (Combination Skin)
                </option>
                <option value="Sensitive Skin" data-id="element-406">
                  संवेदनशील त्वचा (Sensitive Skin)
                </option>
              </select>
              <Input className="mt-2" value={data.skinAdditional} onChange={e => onChange('skinAdditional', e.target.value)} placeholder="Additional skin details..." data-id="element-407" />
            </div>

            <div data-id="element-408">
              <Label data-id="element-409">8. Scalp(Hair) (खोपड़ी(बाल))</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.scalp} onChange={e => onChange('scalp', e.target.value)} data-id="element-410">
                <option value="" data-id="element-411">Select Scalp Type</option>
                <option value="Normal/Balanced Scalp" data-id="element-412">
                  Normal/Balanced Scalp (सामान्य/संतुलित खोपड़ी)
                </option>
                <option value="Oily Scalp" data-id="element-413">Oily Scalp (तैलीय खोपड़ी)</option>
                <option value="Dry Scalp" data-id="element-414">Dry Scalp (रूखी खोपड़ी)</option>
                <option value="Sensitive Scalp" data-id="element-415">
                  Sensitive Scalp (संवेदनशील खोपड़ी)
                </option>
                <option value="Combination Scalp" data-id="element-416">
                  Combination Scalp (मिश्रित खोपड़ी)
                </option>
                <option value="Early Baldness" data-id="element-417">
                  Early Baldness (प्रारंभिक गंजापन)
                </option>
                <option value="Hair Fall" data-id="element-418">Hair Fall (बाल झड़ना)</option>
                <option value="Early Grey Hair" data-id="element-419">
                  Early Grey Hair (जल्दी सफेद बाल)
                </option>
                <option value="Dandruff" data-id="element-420">Dandruff (रूसी)</option>
              </select>
            </div>

            <div data-id="element-421">
              <Label data-id="element-422">9. Body(Hair) (शरीर पर बाल)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.bodyHair} onChange={e => onChange('bodyHair', e.target.value)} data-id="element-423">
                <option value="" data-id="element-424">Select Type</option>
                <option value="No Hair" data-id="element-425">No Hair (कोई बाल नहीं)</option>
                <option value="Less Hair" data-id="element-426">Less Hair (कम बाल)</option>
                <option value="Normal Hair" data-id="element-427">Normal Hair (सामान्य बाल)</option>
                <option value="Excess Hair" data-id="element-428">Excess Hair (अधिक बाल)</option>
              </select>
            </div>

            <div data-id="element-429">
              <Label data-id="element-430">10. Nails (नाखून)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.nails} onChange={e => onChange('nails', e.target.value)} data-id="element-431">
                <option value="" data-id="element-432">Select Nail Condition</option>
                <option value="Normal" data-id="element-433">Normal (सामान्य)</option>
                <option value="Dry and brittle" data-id="element-434">
                  Dry and brittle (सूखे और भंगुर)
                </option>
                <option value="Soft and brittle" data-id="element-435">
                  Soft and brittle (नरम और भंगुर)
                </option>
                <option value="Vertical Lines" data-id="element-436">
                  Vertical Lines (ऊर्ध्वाधर रेखाएं)
                </option>
                <option value="Horizontal lines" data-id="element-437">
                  Horizontal lines (क्षैतिज रेखाएं)
                </option>
                <option value="Discolored" data-id="element-438">Discolored (विवर्ण)</option>
                <option value="Thick" data-id="element-439">Thick (मोटे)</option>
                <option value="Thin" data-id="element-440">Thin (पतले)</option>
              </select>
            </div>

            <div data-id="element-441">
              <Label data-id="element-442">11. Body Odor (शरीर की गंध)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.bodyOdour} onChange={e => onChange('bodyOdour', e.target.value)} data-id="element-443">
                <option value="" data-id="element-444">Select Body Odor</option>
                <option value="Normal / No Odor" data-id="element-445">
                  Normal / No Odor (सामान्य / कोई गंध नहीं)
                </option>
                <option value="Fragrant" data-id="element-446">Fragrant (सुगंधित)</option>
                <option value="Woody/Resinous" data-id="element-447">
                  Woody/Resinous (लुड़ी/रेजिनस)
                </option>
                <option value="Minty" data-id="element-448">Minty (पुदीना)</option>
                <option value="Sweet" data-id="element-449">Sweet (मीठा)</option>
                <option value="Chemical" data-id="element-450">Chemical (रासायनिक)</option>
                <option value="Popcorn" data-id="element-451">Popcorn (पॉपकॉर्न)</option>
                <option value="Citrus" data-id="element-452">Citrus (नींबू)</option>
                <option value="Fruity (non-citrus)" data-id="element-453">
                  Fruity (non-citrus) (फल (गैर-साइट्रस))
                </option>
                <option value="Pungent" data-id="element-454">Pungent (तीखा)</option>
                <option value="Putrid" data-id="element-455">Putrid (सड़ा हुआ)</option>
              </select>
            </div>

            <div data-id="element-456">
              <Label data-id="element-457">12. Edema (सूजन)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.edema} onChange={e => onChange('edema', e.target.value)} data-id="element-458">
                <option value="" data-id="element-459">Select Edema Type</option>
                <option value="Normal / No Problem" data-id="element-460">
                  Normal / No Problem (सामान्य / कोई समस्या नहीं)
                </option>
                <option value="Pitting edema" data-id="element-461">Pitting edema (गड्ढादार)</option>
                <option value="Non pitted type" data-id="element-462">
                  Non pitted type (गैर-गड्ढेदार)
                </option>
              </select>
            </div>

            <div data-id="element-463">
              <Label data-id="element-464">13. Eye</Label>
              <Input value={data.eye} onChange={e => onChange('eye', e.target.value)} data-id="element-465" />
            </div>

            <div data-id="element-466">
              <Label data-id="element-467">14. Ear</Label>
              <Input value={data.ear} onChange={e => onChange('ear', e.target.value)} data-id="element-468" />
            </div>

            <div data-id="element-469">
              <Label data-id="element-470">15. Mouth Odor (मुंह की गंध)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.mouthOdour} onChange={e => onChange('mouthOdour', e.target.value)} data-id="element-471">
                <option value="" data-id="element-472">Select Mouth Odor</option>
                <option value="Normal / No Odor" data-id="element-473">
                  Normal / No Odor (सामान्य / कोई गंध नहीं)
                </option>
                <option value="Fragrant" data-id="element-474">Fragrant (सुगंधित)</option>
                <option value="Woody/Resinous" data-id="element-475">
                  Woody/Resinous (लुड़ी/रेजिनस)
                </option>
                <option value="Minty" data-id="element-476">Minty (पुदीना)</option>
                <option value="Sweet" data-id="element-477">Sweet (मीठा)</option>
                <option value="Chemical" data-id="element-478">Chemical (रासायनिक)</option>
                <option value="Popcorn" data-id="element-479">Popcorn (पॉपकॉर्न)</option>
                <option value="Citrus" data-id="element-480">Citrus (नींबू)</option>
                <option value="Fruity (non-citrus)" data-id="element-481">
                  Fruity (non-citrus) (फल (गैर-साइट्रस))
                </option>
                <option value="Pungent" data-id="element-482">Pungent (तीखा)</option>
                <option value="Putrid" data-id="element-483">Putrid (सड़ा हुआ)</option>
              </select>
            </div>

            <div data-id="element-484">
              <Label data-id="element-485">16. Mouth (Additional)</Label>
              <Input value={data.mouth} onChange={e => onChange('mouth', e.target.value)} placeholder="Other mouth details" data-id="element-486" />
            </div>

            <div data-id="element-487">
              <Label data-id="element-488">17. Throat (गला)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.throat} onChange={e => onChange('throat', e.target.value)} data-id="element-489">
                <option value="" data-id="element-490">Select Throat Condition</option>
                <option value="Normal / No Problem" data-id="element-491">
                  Normal / No Problem (सामान्य / कोई समस्या नहीं)
                </option>
                <option value="Tonsillitis" data-id="element-492">Tonsillitis (टॉन्सिलाइटिस)</option>
                <option value="Strep throat" data-id="element-493">
                  Strep throat (स्ट्रेप थ्रोट)
                </option>
                <option value="Laryngitis" data-id="element-494">Laryngitis (लैरिंजाइटिस)</option>
                <option value="Pharyngitis" data-id="element-495">Pharyngitis (फैरिंजाइटिस)</option>
              </select>
            </div>

            <div data-id="element-496">
              <Label data-id="element-497">18. Lips (होंठ)</Label>
              <select className="flex h-10 sm:h-11 w-full rounded-lg border-2 border-amber-200 bg-white px-3 sm:px-4 py-2 text-sm sm:text-base text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all print:h-8 print:text-sm print:rounded-md" value={data.lips} onChange={e => onChange('lips', e.target.value)} data-id="element-498">
                <option value="" data-id="element-499">Select Lips Condition</option>
                <option value="Normal / No Problem" data-id="element-500">
                  Normal / No Problem (सामान्य / कोई समस्या नहीं)
                </option>
                <option value="Chapped lips" data-id="element-501">Chapped lips (फटे होंठ)</option>
                <option value="Cold sores (herpes simplex)" data-id="element-502">
                  Cold sores (herpes simplex) (ठंडे घाव (हर्पीज सिम्प्लेक्स))
                </option>
                <option value="Angular cheilitis" data-id="element-503">
                  Angular cheilitis (कोणीय चेइलिटिस)
                </option>
                <option value="Eczematous cheilitis" data-id="element-504">
                  Eczematous cheilitis (एक्जिमेटस चेइलिटिस)
                </option>
                <option value="Infective cheilitis" data-id="element-505">
                  Infective cheilitis (संक्रामक चेइलिटिस)
                </option>
                <option value="Actinic cheilitis" data-id="element-506">
                  Actinic cheilitis (एक्टिनिक चेइलिटिस)
                </option>
              </select>
            </div>
          </div>
        </div>

        {/* Gynaecological History - Only show if gender is Female */}
        {showGynecologicalHistory && <div className="pt-6 border-t-2 border-amber-200" data-id="element-507">
            <div className="flex items-center gap-2 mb-4" data-id="element-508">
              <Baby className="w-5 h-5 text-amber-600" data-id="element-509" />
              <h3 className="text-lg font-bold text-amber-900" data-id="element-510">
                Gynaecological History
              </h3>
            </div>

            {/* Reproductive & Gynecological Conditions */}
            <div className="mb-6" data-id="element-511">
              <h4 className="text-base font-semibold text-amber-900 mb-3" data-id="element-512">
                Reproductive & Gynecological Conditions
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 bg-amber-50/50 p-5 rounded-lg border-2 border-amber-200" data-id="element-513">
                {['None', 'Endometriosis', 'Dysmenorrhea (cramps)', 'Menorrhagia (heavy bleeding)', 'Adenomyosis', 'Anemia', 'Fibroids / Cysts', 'Cancers: Breast cancer, ovarian cancer, and cervical cancer', 'Polycystic Ovary Syndrome (PCOS)', 'Amenorrhea (absent periods)', 'Premenstrual Syndrome (PMS)', 'Urinary Tract Infections (UTIs)', 'Infertility'].map(condition => <label key={condition} className="flex items-center space-x-3 cursor-pointer hover:bg-amber-100/50 p-2 rounded transition-colors" data-id="element-514">
                    <input type="checkbox" className="rounded border-amber-400 text-amber-600 focus:ring-amber-500" checked={(data.gynecologicalConditions || []).includes(condition)} onChange={() => handleGynecologicalConditionToggle(condition)} data-id="element-515" />
                    <span className="text-sm text-stone-700" data-id="element-516">{condition}</span>
                  </label>)}
              </div>
            </div>

            <div className="space-y-4" data-id="element-517">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-518">
                <div data-id="element-519">
                  <Label data-id="element-520">Are You Pregnant? (क्या आप गर्भवती हैं?)</Label>
                  <select className="w-full h-10 rounded-lg border-2 border-amber-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all" value={data.isPregnant} onChange={e => onChange('isPregnant', e.target.value)} data-id="element-521">
                    <option value="No" data-id="element-522">No</option>
                    <option value="Yes" data-id="element-523">Yes</option>
                  </select>
                </div>

                {data.isPregnant === 'Yes' && <div data-id="element-524">
                    <Label data-id="element-525">How many Months</Label>
                    <Input value={data.pregnancyMonths} onChange={e => onChange('pregnancyMonths', e.target.value)} data-id="element-526" />
                  </div>}

                {data.isPregnant === 'No' && <div data-id="element-527">
                    <Label data-id="element-528">Menopause</Label>
                    <select className="w-full h-10 rounded-lg border-2 border-amber-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all" value={data.menopause} onChange={e => onChange('menopause', e.target.value)} data-id="element-529">
                      <option value="No" data-id="element-530">No</option>
                      <option value="Yes" data-id="element-531">Yes</option>
                    </select>
                  </div>}
              </div>

              {/* Show menstruation details only if not pregnant and not in menopause */}
              {data.isPregnant === 'No' && data.menopause === 'No' && <div className="bg-amber-50/50 p-5 rounded-lg border-2 border-amber-200" data-id="element-532">
                  <Label className="mb-4 block text-amber-900 font-bold text-base" data-id="element-533">
                    Mensuration Details
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-534">
                    <div data-id="element-535">
                      <Label data-id="element-536">
                        Menstruation Duration in Days (मासिक धर्म की अवधि दिनों
                        में)
                      </Label>
                      <select className="w-full h-10 rounded-lg border-2 border-amber-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all" value={data.mensurationDuration} onChange={e => onChange('mensurationDuration', e.target.value)} data-id="element-537">
                        <option value="" data-id="element-538">Select Option</option>
                        <option value="Comes before 16 days" data-id="element-539">
                          Comes before 16 days (16 दिनों से पहले आता है)
                        </option>
                        <option value="Comes 16 days" data-id="element-540">
                          Comes 16 days (16 दिनों में आता है)
                        </option>
                        <option value="Comes 17 days" data-id="element-541">
                          Comes 17 days (17 दिनों में आता है)
                        </option>
                        <option value="Comes 18 days" data-id="element-542">
                          Comes 18 days (18 दिनों में आता है)
                        </option>
                        <option value="Comes 19 days" data-id="element-543">
                          Comes 19 days (19 दिनों में आता है)
                        </option>
                        <option value="Comes 20 days" data-id="element-544">
                          Comes 20 days (20 दिनों में आता है)
                        </option>
                        <option value="Comes 21 days" data-id="element-545">
                          Comes 21 days (21 दिनों में आता है)
                        </option>
                        <option value="Comes 22 days" data-id="element-546">
                          Comes 22 days (22 दिनों में आता है)
                        </option>
                        <option value="Comes 23 days" data-id="element-547">
                          Comes 23 days (23 दिनों में आता है)
                        </option>
                        <option value="Comes 24 days" data-id="element-548">
                          Comes 24 days (24 दिनों में आता है)
                        </option>
                        <option value="Comes 25 days" data-id="element-549">
                          Comes 25 days (25 दिनों में आता है)
                        </option>
                        <option value="Comes 26 days" data-id="element-550">
                          Comes 26 days (26 दिनों में आता है)
                        </option>
                        <option value="Comes 27 days" data-id="element-551">
                          Comes 27 days (27 दिनों में आता है)
                        </option>
                        <option value="Comes 28 days" data-id="element-552">
                          Comes 28 days (28 दिनों में आता है)
                        </option>
                        <option value="Comes 29 days" data-id="element-553">
                          Comes 29 days (29 दिनों में आता है)
                        </option>
                        <option value="Comes 30 days" data-id="element-554">
                          Comes 30 days (30 दिनों में आता है)
                        </option>
                        <option value="Comes 31 days" data-id="element-555">
                          Comes 31 days (31 दिनों में आता है)
                        </option>
                        <option value="More than 36 days" data-id="element-556">
                          More than 36 days (36 दिनों से अधिक)
                        </option>
                      </select>
                    </div>

                    <div data-id="element-557">
                      <Label data-id="element-558">Cycle days (चक्र दिवस)</Label>
                      <select className="w-full h-10 rounded-lg border-2 border-amber-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all" value={data.mensurationCycleDays} onChange={e => onChange('mensurationCycleDays', e.target.value)} data-id="element-559">
                        <option value="" data-id="element-560">Select Option</option>
                        <option value="Does not comes without Medicine" data-id="element-561">
                          Does not comes without Medicine (दवा के बिना नहीं आता
                          है)
                        </option>
                        <option value="1 Day" data-id="element-562">1 Day (1 दिन)</option>
                        <option value="2 Days" data-id="element-563">2 Days (2 दिन)</option>
                        <option value="3 Days" data-id="element-564">3 Days (3 दिन)</option>
                        <option value="4 Days" data-id="element-565">4 Days (4 दिन)</option>
                        <option value="5 Days" data-id="element-566">5 Days (5 दिन)</option>
                        <option value="6 Days" data-id="element-567">6 Days (6 दिन)</option>
                        <option value="7 Days" data-id="element-568">7 Days (7 दिन)</option>
                        <option value="More than 7 Days" data-id="element-569">
                          More than 7 Days (7 दिनों से अधिक)
                        </option>
                        <option value="Does not stops without medicine" data-id="element-570">
                          Does not stops without medicine (दवा के बिना रुकता
                          नहीं है)
                        </option>
                      </select>
                    </div>

                    <div data-id="element-571">
                      <Label data-id="element-572">Painful (दर्दनाक)</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2" data-id="element-573">
                        {['No Pain', 'Before', 'During', 'After'].map(option => <label key={option} className="flex items-center space-x-2 cursor-pointer hover:bg-amber-100/50 p-2 rounded transition-colors" data-id="element-574">
                              <input type="checkbox" className="rounded border-amber-400 text-amber-600 focus:ring-amber-500" checked={(data.mensurationPainful || []).includes(option)} onChange={() => handlePainfulToggle(option)} data-id="element-575" />
                              <span className="text-sm text-stone-700" data-id="element-576">
                                {option === 'No Pain' && 'No Pain (कोई दर्द नहीं)'}
                                {option === 'Before' && 'Before (पहले)'}
                                {option === 'During' && 'During (दौरान)'}
                                {option === 'After' && 'After (बाद)'}
                              </span>
                            </label>)}
                      </div>
                    </div>

                    <div data-id="element-577">
                      <Label data-id="element-578">Clots (थक्के)</Label>
                      <select className="w-full h-10 rounded-lg border-2 border-amber-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all" value={data.mensurationClots} onChange={e => onChange('mensurationClots', e.target.value)} data-id="element-579">
                        <option value="" data-id="element-580">Select Option</option>
                        <option value="Yes" data-id="element-581">Yes (हां)</option>
                        <option value="No" data-id="element-582">No (नहीं)</option>
                      </select>
                    </div>

                    <div data-id="element-583">
                      <Label data-id="element-584">Type of Flow (प्रवाह का प्रकार)</Label>
                      <select className="w-full h-10 rounded-lg border-2 border-amber-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all" value={data.mensurationFlow} onChange={e => onChange('mensurationFlow', e.target.value)} data-id="element-585">
                        <option value="" data-id="element-586">Select Flow Type</option>
                        <option value="Scanty" data-id="element-587">Scanty (अल्प)</option>
                        <option value="Normal" data-id="element-588">Normal (सामान्य)</option>
                        <option value="Excess" data-id="element-589">Excess (अधिकता)</option>
                      </select>
                    </div>
                  </div>
                </div>}
            </div>
          </div>}

        {/* Past Illness */}
        <div className="pt-6 border-t border-slate-100" data-id="element-590">
          <Label className="text-base font-semibold" data-id="element-591">
            History of Past Illness
          </Label>
          <Input className="mt-2" value={data.pastIllness} onChange={e => onChange('pastIllness', e.target.value)} placeholder="Previous major illnesses, surgeries, etc." data-id="element-592" />
        </div>
      </CardContent>
    </Card>;
}